import { rowSlugs } from './slugs';
import {
  CAPACITY_UNIT_BY_TOKEN,
  ENERGY_CAPACITY_UNITS,
  isEnergyProduct,
  teaProductSlugs,
} from './tea';

// Units that allow negative values (temperatures can legitimately be < 0;
// `scalar` is a free-form coefficient and may be either sign).
const SIGNED_PARAM_UNITS = new Set(['degC', 'K', 'scalar']);

// Inputs, outputs and params are each slug-deduped independently — on this
// side and on the backend — so two rows in the *same* list are fine; they
// become `gas` and `gas_2`. The same slug in two different lists is not: the
// parser raises "slug collisions across param/input/output" (see
// `collisions` in core/pathway/equation_parser.py) because a balance
// expression could not say which one it meant. That used to reach the user
// as a raw server error at save time, with nothing on the form to point at.
function collectSlugCollisions({ inputs, outputs, params }) {
  const namespaces = [
    { key: 'inputs', noun: 'input', rows: inputs },
    { key: 'outputs', noun: 'output', rows: outputs },
    { key: 'params', noun: 'parameter', rows: params },
  ];

  const owners = new Map();
  namespaces.forEach(({ key, noun, rows }) => {
    rowSlugs(rows).forEach((slug, idx) => {
      // Blank labels already raise their own "missing a label" error.
      if (!slug) return;
      if (!owners.has(slug)) owners.set(slug, []);
      owners.get(slug).push({ key, noun, idx, label: rows[idx]?.label || slug, named: !!rows[idx]?.name });
    });
  });

  const collisions = [];
  owners.forEach((rows, slug) => {
    if (new Set(rows.map(r => r.key)).size < 2) return;
    const described = rows.map(r => `${r.noun} "${r.label}"`).join(', ');
    // Relabelling a row that keeps a saved name doesn't change its slug, so
    // point at a row whose label still decides it.
    const target = rows.find(r => !r.named) ?? rows[0];
    collisions.push({
      path: `${target.key}.${target.idx}.label`,
      message: `Slug "${slug}" is claimed by ${described}. Inputs, outputs and parameters share one namespace in the balance — rename one of them.`,
    });
  });
  return collisions;
}

// Validators across three tiers. Severity `'error'` blocks Save; `'warning'`
// surfaces in a non-blocking banner.
//
// - Tier 1 (structural):  blank labels, missing anchor, non-allocatable
//                         outputs, malformed defaults.
// - Tier 2 (physical):    `%` defaults outside [0, 1], negative defaults on
//                         physical quantities.
// - Tier 3 (semantic):    outputs never assigned by the balance; params
//                         never referenced. Heuristic — matches identifiers
//                         in the raw text rather than re-parsing.
export function collectValidationIssues({ label, inputs, outputs, params, balance, tea }) {
  const issues = [];
  const push = (severity, path, message) => issues.push({ severity, path, message });

  if (!label || !label.trim()) {
    push('error', 'label', 'Module label is required.');
  }
  if (!balance || !balance.trim()) {
    push('error', 'balance', 'Balance is empty.');
  }

  const inputList = inputs ?? [];
  const outputList = outputs ?? [];

  if (inputList.length === 0) {
    push('error', 'inputs', 'At least one input is required (the first is the balance anchor).');
  }
  // A module with no outputs produces nothing to connect or allocate to, and
  // the backend rejects it. Without this the form silently allowed Save and
  // surfaced the failure as a raw server error.
  if (outputList.length === 0) {
    push('error', 'outputs', 'At least one output is required.');
  }
  inputList.forEach((io, idx) => {
    if (!io?.label || !io.label.trim()) {
      push('error', `inputs.${idx}.label`, `Input #${idx + 1} is missing a label.`);
    }
  });
  outputList.forEach((io, idx) => {
    if (!io?.label || !io.label.trim()) {
      push('error', `outputs.${idx}.label`, `Output #${idx + 1} is missing a label.`);
    }
  });

  collectSlugCollisions({ inputs: inputList, outputs: outputList, params: params ?? [] })
    .forEach(collision => push('error', collision.path, collision.message));

  const allocatableOutputs = outputList.filter(io => !io?.emissions && io?.allocatable !== false);
  if (outputList.length > 0 && allocatableOutputs.length === 0) {
    push(
      'error',
      'outputs',
      'At least one output must be allocatable (Sesame attributes LCA / TEA impacts to allocatable products).',
    );
  }
  // Allocation across products of incompatible dimensions fails at LCA
  // analysis time ("Allocatable IOs cannot all be converted to ..."). Warn
  // when the user has allocatable outputs spanning energy + mass species so
  // they can mark byproducts as non-allocatable before saving.
  if (allocatableOutputs.length >= 2) {
    const energyAllocatable = allocatableOutputs.filter(
      io => isEnergyProduct(io.species),
    );
    const massAllocatable = allocatableOutputs.filter(
      io => !isEnergyProduct(io.species),
    );
    if (energyAllocatable.length > 0 && massAllocatable.length > 0) {
      const labels = allocatableOutputs.map(io => `"${io.label}"`).join(', ');
      push(
        'warning',
        'outputs',
        `Allocatable outputs span energy and mass species (${labels}). LCA allocation requires a common dimension — uncheck "Alloc" on byproducts you don't want to share environmental burden with.`,
      );
    }
  }

  (params ?? []).forEach((p, idx) => {
    if (!p?.label || !p.label.trim()) {
      push('error', `params.${idx}.label`, `Parameter #${idx + 1} is missing a label.`);
      return;
    }
    if (p.type === 'boolean') {
      if (p.default !== 'true' && p.default !== 'false' && p.default !== true && p.default !== false) {
        push('error', `params.${idx}.default`, `Boolean param "${p.label}" default must be true or false.`);
      }
      return;
    }
    const num = parseFloat(p.default);
    if (!Number.isFinite(num)) {
      push('error', `params.${idx}.default`, `Numeric param "${p.label}" default is not a number.`);
      return;
    }
    // Tier 2: physical-sanity warnings on numeric defaults.
    if (p.unit === '%') {
      if (num < 0 || num > 1) {
        push(
          'warning',
          `params.${idx}.default`,
          `"${p.label}" is a fraction (0–1) but default is ${num}. Did you mean ${(num / 100).toString()}?`,
        );
      }
    } else if (p.unit && !SIGNED_PARAM_UNITS.has(p.unit) && num < 0) {
      push(
        'warning',
        `params.${idx}.default`,
        `"${p.label}" has a negative default (${num} ${p.unit}). Physical quantities are usually ≥ 0.`,
      );
    }
  });

  // Tier 1: TEA-mode completeness. Without these, `buildTeaPayload` silently
  // drops the TEA configuration at submit time and the user has no idea
  // their economics weren't saved.
  const teaMode = tea?.mode || 'none';
  // Same slugs the TEA form renders and the payload sends — see teaProductSlugs.
  const outputTeaSlugs = teaProductSlugs(outputList);
  if (teaMode === 'simple') {
    const nonEmissionsOutputs = outputList.filter(o => !o?.emissions);
    const hasAnyCost = outputList.some((io, idx) => {
      if (io?.emissions) return false;
      const cost = parseFloat(tea?.simple_costs?.[outputTeaSlugs[idx]]);
      return Number.isFinite(cost) && cost > 0;
    });
    if (nonEmissionsOutputs.length > 0 && !hasAnyCost) {
      push(
        'error',
        'tea.simple_costs',
        'Simple TEA: set a cost on at least one product output, or switch TEA mode to None.',
      );
    }
  } else if (teaMode === 'dcfa') {
    if (!tea?.reference_product) {
      push(
        'error',
        'tea.reference_product',
        'DCFA TEA: pick a reference product.',
      );
    } else {
      // Reference product must still exist as an allocatable output.
      const validSlug = outputList.some(
        (o, idx) =>
          !o?.emissions &&
          o?.allocatable !== false &&
          outputTeaSlugs[idx] === tea.reference_product,
      );
      if (!validSlug) {
        push(
          'error',
          'tea.reference_product',
          'DCFA TEA: reference product no longer matches an allocatable output. Re-select.',
        );
      }
    }
    const refCapacity = parseFloat(tea?.reference_capacity);
    if (!Number.isFinite(refCapacity) || refCapacity <= 0) {
      push(
        'error',
        'tea.reference_capacity',
        'DCFA TEA: reference capacity must be greater than zero.',
      );
    }
    if (!tea?.reference_capacity_unit || !CAPACITY_UNIT_BY_TOKEN[tea.reference_capacity_unit]) {
      push(
        'error',
        'tea.reference_capacity_unit',
        'DCFA TEA: pick a capacity unit.',
      );
    }
    // Reference product / capacity unit dimension match. Power units (MW,
    // GW) and energy/year units imply an energy product (electricity);
    // mass-flow units imply a mass product. Catches the agent picking a
    // CO2 stream as reference when capacity is in MW.
    if (tea?.reference_product && tea?.reference_capacity_unit) {
      const refOutput = outputList.find(
        (o, idx) => !o?.emissions && outputTeaSlugs[idx] === tea.reference_product,
      );
      if (refOutput) {
        const capUnit = tea.reference_capacity_unit;
        const energyCap = ENERGY_CAPACITY_UNITS.has(capUnit);
        const energyProduct = isEnergyProduct(refOutput.species);
        if (energyCap && !energyProduct) {
          push(
            'error',
            'tea.reference_product',
            `DCFA TEA: capacity unit ${capUnit} is energy/power but reference product "${refOutput.label}" is a mass product. Pick an electricity/heat output, or switch capacity unit to a mass-flow unit (ton/day, kg/hr, etc.).`,
          );
        } else if (!energyCap && energyProduct) {
          push(
            'error',
            'tea.reference_product',
            `DCFA TEA: capacity unit ${capUnit} is mass-flow but reference product "${refOutput.label}" is electricity/heat. Switch capacity unit to MW/GW/MWh-year, or pick a mass product.`,
          );
        }
      }
    }
  }

  // Tier 2: TEA physical-sanity warnings.
  if (teaMode === 'dcfa') {
    const cf = parseFloat(tea?.capacity_factor);
    if (Number.isFinite(cf) && (cf < 0 || cf > 1)) {
      push(
        'warning',
        'tea.capacity_factor',
        `Capacity factor is a fraction (0–1) but is ${cf}.`,
      );
    }
    const exp = parseFloat(tea?.scaling_exponent);
    if (Number.isFinite(exp) && exp < 0) {
      push(
        'warning',
        'tea.scaling_exponent',
        `Scaling exponent is negative (${exp}). Typical industrial values are 0.6–0.7; 0 disables scaling.`,
      );
    }
  }

  // Tier 3: semantic checks against the balance text. Skip if any Tier 1
  // structural error already blocks — re-parsing is wasted effort.
  const blocked = issues.some(i => i.severity === 'error');
  if (!blocked && balance && balance.trim()) {
    const outputSlugs = rowSlugs(outputList);
    const paramSlugs = rowSlugs(params);

    // Strip comments and assert-message string literals so identifiers
    // mentioned only in human-readable text don't count as "used".
    const stripped = balance
      .split('\n')
      .map(line => line.replace(/#.*$/, ''))
      .join('\n')
      .replace(/"[^"]*"/g, '')
      .replace(/'[^']*'/g, '');

    const lhsSet = new Set();
    for (const rawLine of stripped.split('\n')) {
      const m = rawLine.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*=(?!=)/);
      if (m) lhsSet.add(m[1]);
    }

    const usesCloseCarbon = /\bclose_carbon_balance\s*\(/.test(stripped);

    outputSlugs.forEach((slug, idx) => {
      if (lhsSet.has(slug)) return;
      // `close_carbon_balance()` typically assigns the last carbon-bearing
      // output by aliasing it as the LHS. Skip the warning for outputs that
      // could plausibly be the helper's target (any output not already
      // assigned, when the balance does call the helper).
      if (usesCloseCarbon) return;
      const ioLabel = outputList[idx]?.label || slug;
      push(
        'warning',
        `outputs.${idx}.label`,
        `Output "${ioLabel}" is declared but never assigned in the balance.`,
      );
    });

    paramSlugs.forEach((slug, idx) => {
      const usageRe = new RegExp(`\\b${slug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
      // Only count usage outside of LHS positions to avoid a `param = param`
      // false-positive — but params can't appear as LHS anyway (parser
      // rejects), so any match is real usage.
      if (usageRe.test(stripped)) return;
      const paramLabel = params[idx]?.label || slug;
      push(
        'warning',
        `params.${idx}.label`,
        `Parameter "${paramLabel}" is declared but never used in the balance.`,
      );
    });
  }

  const hasBlocking = issues.some(i => i.severity === 'error');
  return { issues, hasBlocking };
}
